import type { Stripe } from '@stripe/stripe-js'
import { loadStripe } from '@stripe/stripe-js/pure'
import { useEffect, useState } from 'react'

// Memoized Stripe promise that handles missing key and load failures gracefully
let stripePromise: Promise<Stripe | null> | null = null

const getStripePromise = () => {
  if (stripePromise) return stripePromise

  const key = import.meta.env.VITE_STRIPE_PUBLIC_KEY
  if (!key) {
    // Return resolved null instead of rejecting - prevents Sentry errors in staging
    stripePromise = Promise.resolve(null)
    return stripePromise
  }

  stripePromise = loadStripe(key).catch(() => {
    // Handle network failures (ad blockers, firewalls, etc.) gracefully
    return null
  })

  return stripePromise
}

/**
 * Hook to load Stripe on demand with proper loading/error states.
 * Follows conditional loading pattern - only loads when enabled.
 *
 * @param enabled - Whether to load Stripe (e.g., when dialog opens)
 * @returns stripe instance, loading state, and error state
 */
export function useStripeLoader(enabled: boolean) {
  // undefined = not started, null = failed, Stripe = success
  const [stripe, setStripe] = useState<Stripe | null | undefined>(undefined)

  useEffect(() => {
    if (enabled) {
      getStripePromise().then(setStripe)
    } else {
      setStripe(undefined)
    }
  }, [enabled])

  return {
    stripe,
    isLoading: enabled && stripe === undefined,
    isError: stripe === null,
  }
}
